import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const mono = { fontFamily: "'DM Mono', monospace" };
const sans = { fontFamily: "'DM Sans', sans-serif" };

const sectionLabel = {
  ...mono, fontSize: 7, letterSpacing: '0.22em', textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.25)', padding: '16px 16px 6px',
};

const linkBase = {
  display: 'block', ...mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
  textDecoration: 'none', padding: '10px 16px',
  color: 'rgba(255,255,255,0.55)',
  borderLeft: '2px solid transparent',
  transition: 'color 0.15s, border-color 0.15s, background 0.15s',
};

const linkActive = {
  color: 'rgba(255,255,255,0.90)',
  borderLeftColor: '#C4956A',
  background: 'rgba(255,255,255,0.05)',
};

const separator = { height: 1, background: 'rgba(255,255,255,0.06)', margin: '8px 16px' };

function NavItem({ to, label, onClick, indent, badge }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      style={({ isActive }) => ({
        ...linkBase,
        ...(isActive ? linkActive : {}),
        ...(indent ? { paddingLeft: 32 } : {}),
        ...(badge ? { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } : {}),
      })}
      onMouseEnter={e => {
        if (e.currentTarget.getAttribute('aria-current') !== 'page')
          e.currentTarget.style.color = 'rgba(255,255,255,0.92)';
      }}
      onMouseLeave={e => {
        if (e.currentTarget.getAttribute('aria-current') !== 'page')
          e.currentTarget.style.color = 'rgba(255,255,255,0.65)';
      }}
    >
      <span>{label}</span>
      {badge}
    </NavLink>
  );
}

// ── Engagement Dropdown ──────────────────────────────────────────────────

function EngagementDropdown({ activeEngagementId, onSelect }) {
  const [engagements, setEngagements] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    supabase
      .from('cordis_engagements')
      .select('id,client_name,company,current_stage')
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (data) setEngagements(data);
      });
  }, []);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selected = engagements.find(e => e.id === activeEngagementId);

  return (
    <div ref={ref} style={{ position: 'relative', padding: '0 12px' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(122,82,48,0.2)', borderRadius: 0,
          padding: '10px 12px', cursor: 'pointer', textAlign: 'left',
        }}
      >
        {selected ? (
          <>
            <div style={{ ...sans, fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.85)', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {selected.client_name}
            </div>
            <div style={{ ...mono, fontSize: 8, letterSpacing: '0.14em', color: '#C4956A', textTransform: 'uppercase', marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {selected.company}
            </div>
          </>
        ) : (
          <div style={{ ...mono, fontSize: 9, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.3)' }}>
            Select engagement...
          </div>
        )}
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 12, right: 12,
          marginTop: 4, background: '#0D2438',
          border: '1px solid rgba(122,82,48,0.2)', borderRadius: 0,
          maxHeight: 300, overflowY: 'auto', zIndex: 200,
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}>
          {engagements.map(e => {
            const isSelected = e.id === activeEngagementId;
            return (
              <div
                key={e.id}
                style={{
                  padding: '10px 12px', cursor: 'pointer',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                  background: isSelected ? 'rgba(122,82,48,0.12)' : 'transparent',
                  transition: 'background 0.1s',
                }}
                onClick={() => { onSelect(e.id); setOpen(false); }}
                onMouseEnter={ev => { ev.currentTarget.style.background = 'rgba(122,82,48,0.08)'; }}
                onMouseLeave={ev => {
                  ev.currentTarget.style.background = isSelected ? 'rgba(122,82,48,0.12)' : 'transparent';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ ...sans, fontSize: 13, color: isSelected ? '#C4956A' : 'rgba(255,255,255,0.85)', lineHeight: 1.3 }}>
                    {e.client_name}
                  </div>
                  {e.current_stage && (
                    <div style={{
                      ...mono, fontSize: 7, letterSpacing: '0.08em',
                      color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase',
                      background: 'rgba(255,255,255,0.06)', padding: '2px 6px',
                      flexShrink: 0, marginLeft: 8,
                    }}>
                      {e.current_stage.replace(/_/g, ' ')}
                    </div>
                  )}
                </div>
                <div style={{ ...mono, fontSize: 9, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
                  {e.company}
                </div>
              </div>
            );
          })}
          {engagements.length === 0 && (
            <div style={{ padding: '10px 12px', ...mono, fontSize: 9, color: 'rgba(255,255,255,0.25)' }}>
              No engagements found
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Notification Bell ────────────────────────────────────────────────────

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    fetch('/api/notifications', { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(data => setItems(Array.isArray(data) ? data : []))
      .catch(() => {});
    const interval = setInterval(() => {
      fetch('/api/notifications', { credentials: 'include' })
        .then(r => r.ok ? r.json() : [])
        .then(data => setItems(Array.isArray(data) ? data : []))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const unread = items.filter(i => !i.read).length;

  const markRead = () => {
    fetch('/api/notifications/mark-read', { method: 'POST', credentials: 'include' }).catch(() => {});
    setItems(prev => prev.map(i => ({ ...i, read: true })));
  };

  const actorColor = (a) => {
    const al = (a || '').toLowerCase();
    if (al === 'system' || al === 'erin') return { bg: 'rgba(122,82,48,0.15)', fg: '#7A5230' };
    if (al === 'client') return { bg: 'rgba(42,95,63,0.15)', fg: '#2A5F3F' };
    return { bg: 'rgba(10,24,40,0.10)', fg: 'rgba(255,255,255,0.6)' };
  };

  function timeAgo(ts) {
    if (!ts) return '';
    const diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, position: 'relative', minWidth: 32, minHeight: 32 }}>
        <span style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }}>{'\u{1F514}'}</span>
        {unread > 0 && (
          <span style={{ position: 'absolute', top: 2, right: 2, background: '#7A5230', color: '#F5F0E8', ...mono, fontSize: 8, width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', right: 0, width: 300, background: '#FFFFFF', border: '1px solid rgba(7,25,42,0.10)', boxShadow: '0 8px 32px rgba(0,0,0,0.08)', zIndex: 200, maxHeight: 400, overflowY: 'auto' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(7,25,42,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ ...mono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#7A5230' }}>NOTIFICATIONS</span>
            {unread > 0 && (
              <button onClick={markRead} style={{ ...mono, fontSize: 8, color: '#7A94A8', background: 'none', border: 'none', cursor: 'pointer' }}>Mark all read</button>
            )}
          </div>
          {items.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', ...mono, fontSize: 10, color: '#7A94A8' }}>No notifications</div>
          ) : items.map((n, i) => {
            const ac = actorColor(n.actor);
            return (
              <div key={n.id || i} style={{ padding: '10px 16px', borderBottom: '1px solid rgba(7,25,42,0.06)', opacity: n.read ? 0.5 : 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ ...mono, fontSize: 7, letterSpacing: '0.08em', padding: '2px 6px', background: ac.bg, color: ac.fg, textTransform: 'uppercase' }}>{n.actor}</span>
                  <span style={{ ...mono, fontSize: 8, color: '#7A94A8' }}>{timeAgo(n.created_at)}</span>
                </div>
                {n.client_name && <div style={{ ...sans, fontSize: 12, color: '#0A1828', marginBottom: 2 }}>{n.client_name}</div>}
                <div style={{ ...sans, fontSize: 11, color: '#4A6278' }}>{n.detail || n.action}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main Nav ─────────────────────────────────────────────────────────────

export default function Nav({ advisorName, advisorRole }) {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeEngagementId, setActiveEngagementId] = useState(
    () => localStorage.getItem('cordis_active_engagement_id') || ''
  );
  const [unassignedCases, setUnassignedCases] = useState(0);

  useEffect(() => {
    supabase
      .from('cordis_cases')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'unassigned')
      .then(({ count }) => { if (typeof count === 'number') setUnassignedCases(count); })
      .catch(() => {});
    const interval = setInterval(() => {
      supabase
        .from('cordis_cases')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'unassigned')
        .then(({ count }) => { if (typeof count === 'number') setUnassignedCases(count); })
        .catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handler = () => setMobileOpen(false);
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  const closeMobile = () => setMobileOpen(false);

  const handleEngagementSelect = (id) => {
    setActiveEngagementId(id);
    localStorage.setItem('cordis_active_engagement_id', id);
    navigate(`/overview/${id}`);
  };

  const casesBadge = unassignedCases > 0 ? (
    <span style={{ ...mono, fontSize: 8, padding: '1px 6px', background: 'rgba(184,144,68,0.25)', color: '#B89044' }}>
      {unassignedCases}
    </span>
  ) : null;

  return (
    <>
      {/* Mobile hamburger bar */}
      <div className="mobile-topbar">
        <button className="hamburger" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle navigation">
          <span /><span /><span />
        </button>
        <div style={{ ...mono, fontSize: 10, fontWeight: 500, letterSpacing: '0.28em', color: '#C4956A', textTransform: 'uppercase' }}>CORDIS MRI</div>
        <NotificationBell />
      </div>

      {mobileOpen && <div className="nav-overlay" onClick={closeMobile} />}

      <nav className={`nav-sidebar ${mobileOpen ? 'nav-open' : ''}`}>
        {/* Logo */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 16px 16px' }}>
          <div style={{ ...mono, fontSize: 11, fontWeight: 500, letterSpacing: '0.28em', color: '#C4956A', textTransform: 'uppercase' }}>CORDIS MRI</div>
          <div className="desktop-bell"><NotificationBell /></div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Main nav */}
          <NavItem to="/dashboard" label="DASHBOARD" onClick={closeMobile} />
          <NavItem to="/pipeline" label="PIPELINE" onClick={closeMobile} />
          <NavItem to="/cases" label="CASES" onClick={closeMobile} badge={casesBadge} />

          {/* Engagement section */}
          <div style={separator} />
          <div style={sectionLabel}>ENGAGEMENT</div>

          <EngagementDropdown
            activeEngagementId={activeEngagementId}
            onSelect={handleEngagementSelect}
          />

          {activeEngagementId && (
            <div style={{ marginTop: 4 }}>
              <NavItem to={`/overview/${activeEngagementId}`} label="OVERVIEW" onClick={closeMobile} indent />
              <NavItem to={`/lab/${activeEngagementId}`} label="LAB" onClick={closeMobile} indent />
            </div>
          )}

          {/* Admin section */}
          <div style={separator} />
          <div style={sectionLabel}>ADMIN</div>
          <NavItem to="/contacts" label="CONTACTS" onClick={closeMobile} />
          <NavItem to="/settings" label="SETTINGS" onClick={closeMobile} />
          <div style={separator} />
          <div style={sectionLabel}>COACHING</div>
          <NavItem to="/hockey" label="HOCKEY INTEL" onClick={closeMobile} />
        </div>

        {advisorName && (
          <div style={{ ...mono, fontSize: 9, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.25)', padding: '16px 20px' }}>
            {advisorName}
          </div>
        )}
      </nav>

      <style>{`
        .nav-sidebar {
          position: fixed; top: 0; left: 0; width: 220px; height: 100vh;
          background: #061524; display: flex; flex-direction: column;
          z-index: 100; border-right: 1px solid rgba(255,255,255,0.06);
          transition: transform 0.25s ease;
        }
        .mobile-topbar { display: none; }
        .nav-overlay { display: none; }
        .desktop-bell { display: block; }
        @media (max-width: 768px) {
          .nav-sidebar { transform: translateX(-100%); }
          .nav-sidebar.nav-open { transform: translateX(0); }
          .mobile-topbar {
            display: flex; align-items: center; justify-content: space-between;
            position: fixed; top: 0; left: 0; right: 0; height: 52px;
            background: #061524; padding: 0 16px; z-index: 99;
            border-bottom: 1px solid rgba(255,255,255,0.06);
          }
          .hamburger {
            background: none; border: none; cursor: pointer;
            display: flex; flex-direction: column; gap: 4px;
            padding: 8px; min-width: 48px; min-height: 48px;
            align-items: center; justify-content: center;
          }
          .hamburger span { display: block; width: 18px; height: 2px; background: rgba(255,255,255,0.6); }
          .nav-overlay { display: block; position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 99; }
          .desktop-bell { display: none; }
        }
      `}</style>
    </>
  );
}
